import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, Btn, StatCard } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Building2, Plus, MapPin, X, KeyRound, Copy, Settings, Upload, Cloud, AlertTriangle, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { timeAgo } from "@/lib/ui";

export default function CustomerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [c, setC] = useState(null);
  const [showSite, setShowSite] = useState(false);
  const [siteName, setSiteName] = useState("");
  const [showTok, setShowTok] = useState(false);
  const [tok, setTok] = useState(null);
  const [tokSite, setTokSite] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [sf, setSf] = useState({});
  const [tab, setTab] = useState("Sites");
  const [td, setTd] = useState([]);
  const [syncNonce, setSyncNonce] = useState(0);
  const TABS = ["Sites", "People", "Devices", "Assets", "Tickets", "Alerts"];
  const TAB_EP = { People: "/people", Devices: "/devices", Assets: "/assets", Tickets: "/tickets", Alerts: "/alerts" };

  const load = () => api.get("/customers/" + id).then((r) => setC(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (tab === "Sites") { setTd([]); return; }
    api.get(TAB_EP[tab], { params: { customer_id: id } }).then((r) => setTd(r.data)).catch(() => setTd([]));
    /* eslint-disable-next-line */
  }, [tab, id]);

  const openSettings = () => {
    setSf({ name: c.name || "", notes: c.notes || "", portal_welcome: c.portal_welcome || "", blended_rate: c.blended_rate || "",
            azure_tenant_id: c.azure_tenant_id || "", azure_client_id: c.azure_client_id || "",
            azure_client_secret: "", logo: null });
    setShowSettings(true);
  };
  const onLogo = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setSf((s) => ({ ...s, logo: reader.result }));
    reader.readAsDataURL(file);
  };
  const saveSettings = async () => {
    const body = { name: sf.name, notes: sf.notes, portal_welcome: sf.portal_welcome,
                   blended_rate: sf.blended_rate === "" ? 0 : parseFloat(sf.blended_rate) || 0,
                   azure_tenant_id: sf.azure_tenant_id, azure_client_id: sf.azure_client_id };
    if (sf.azure_client_secret) body.azure_client_secret = sf.azure_client_secret;
    if (sf.logo) body.logo = sf.logo;
    try { await api.patch("/customers/" + id, body); toast.success("Customer settings saved"); setShowSettings(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const addSite = async () => {
    try { await api.post("/sites", { customer_id: id, name: siteName }); toast.success("Site added"); setShowSite(false); setSiteName(""); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const genToken = async () => {
    try {
      const { data } = await api.post("/enrollment-tokens", { customer_id: id, site_id: tokSite || null, expires_in_hours: 24, max_uses: 5 });
      setTok(data.token);
      toast.success("Enrollment token generated");
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!c) return <div className="p-6"><Spinner /></div>;
  return (
    <div className="p-6 fade-up">
      <button onClick={() => nav("/customers")} className="text-[12px] text-slate-500 hover:text-cyan-400 mb-2">← Customers</button>
      <PageHeader title={c.name} subtitle={c.notes || "Customer overview"} testid="customer-detail-page"
        actions={<>
          {can("customers:edit") && <Btn data-testid="customer-settings-btn" variant="ghost" onClick={openSettings}><Settings size={15} /> Settings</Btn>}
          {can("devices:install_agent") && <Btn data-testid="gen-token-btn" variant="ghost" onClick={() => { setShowTok(true); setTok(null); }}><KeyRound size={15} /> Enrollment Token</Btn>}
          {can("customers:edit") && <Btn data-testid="add-site-btn" onClick={() => setShowSite(true)}><Plus size={15} /> Add Site</Btn>}
        </>} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Devices" value={c.device_count} tone="cyan" />
        <StatCard label="Online" value={c.online_count} tone="emerald" />
        <StatCard label="Open Alerts" value={c.open_alerts} tone="rose" />
        <StatCard label="Open Tickets" value={c.open_tickets} tone="sky" />
      </div>

      <div className="flex gap-1 border-b border-slate-800 mb-4 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} data-testid={`cust-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
            className={"px-3 py-2 text-[13px] font-medium whitespace-nowrap border-b-2 -mb-px transition-colors " +
              (tab === t ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-slate-200")}>{t}</button>
        ))}
      </div>

      {tab === "Sites" && (
        <>
          <h2 className="font-heading font-bold text-slate-200 mb-2 flex items-center gap-2"><MapPin size={16} className="text-cyan-400" /> Sites</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {c.sites.length === 0 && <div className="text-sm text-slate-600 col-span-full py-4">No sites yet.</div>}
            {c.sites.map((s) => (
              <div key={s.id} onClick={() => nav("/devices?site_id=" + s.id)}
                className="card-surface rounded-lg p-4 cursor-pointer hover:border-cyan-800 transition-colors" data-testid="site-card">
                <div className="font-heading font-semibold text-slate-100">{s.name}</div>
                <div className="text-[11px] text-slate-500 mt-1">{s.location || "—"}</div>
              </div>
            ))}
          </div>
        </>
      )}
      {tab === "People" && <>
        <CustomerEntra id={id} canEdit={can("customers:edit")} onSynced={() => setSyncNonce((n) => n + 1)} />
        <AutoLinkReport id={id} nonce={syncNonce} canEdit={can("devices:modify")} />
        <DirectoryGaps id={id} nonce={syncNonce} onNav={(p) => nav(p)} />
        <Tbl testid="cust-people" rows={td} onRow={(r) => nav(`/people/${r.id}`)}
          cols={[["name", "Name"], ["email", "Email"], ["title", "Title"], ["department", "Department"], ["device_count", "Devices"]]} empty="No people for this customer." />
      </>}
      {tab === "Devices" && <Tbl testid="cust-devices" rows={td} onRow={(r) => nav(`/devices/${r.id}`)}
        cols={[["hostname", "Hostname"], ["os", "OS"], ["device_type", "Type"], ["status", "Status"], ["local_ip", "IP"]]} empty="No devices for this customer." />}
      {tab === "Assets" && <Tbl testid="cust-assets" rows={td}
        cols={[["name", "Asset"], ["asset_type", "Type"], ["vendor", "Vendor"], ["serial_number", "Serial"], ["assigned_contact_name", "Assigned to"], ["status", "Status"]]} empty="No assets for this customer." />}
      {tab === "Tickets" && <Tbl testid="cust-tickets" rows={td}
        cols={[["number", "#"], ["subject", "Subject"], ["ticket_type", "Type"], ["priority", "Priority"], ["status", "Status"]]} empty="No tickets for this customer." />}
      {tab === "Alerts" && <Tbl testid="cust-alerts" rows={td}
        cols={[["title", "Alert"], ["severity", "Severity"], ["hostname", "Device"], ["category", "Category"], ["status", "Status"]]} empty="No alerts for this customer." />}

      {showSettings && (
        <Modal title="Customer Settings" onClose={() => setShowSettings(false)}>
          <div className="max-h-[70vh] overflow-y-auto pr-1">
            <label className="lbl">Name</label>
            <input data-testid="cust-name" value={sf.name} onChange={(e) => setSf({ ...sf, name: e.target.value })} className="inp2" />
            <label className="lbl">Notes</label>
            <input value={sf.notes} onChange={(e) => setSf({ ...sf, notes: e.target.value })} className="inp2" />
            <label className="lbl">Blended labor rate ($/hr) — overrides the global rate for this customer</label>
            <input data-testid="cust-rate" type="number" value={sf.blended_rate} onChange={(e) => setSf({ ...sf, blended_rate: e.target.value })} className="inp2" placeholder="Leave blank to use global rate" />

            <div className="flex items-center gap-2 mt-4 mb-2"><Upload size={14} className="text-cyan-400" /><span className="font-heading font-bold text-slate-200 text-[13px]">Portal Branding</span></div>
            <label className="lbl">Portal welcome message</label>
            <input data-testid="cust-welcome" value={sf.portal_welcome} onChange={(e) => setSf({ ...sf, portal_welcome: e.target.value })} className="inp2" placeholder="Shown at the top of the customer portal" />
            <label className="lbl">Logo {c.has_logo && <span className="text-emerald-400">(current logo set)</span>}</label>
            <input data-testid="cust-logo" type="file" accept="image/*" onChange={onLogo} className="inp2 text-slate-400" />
            {sf.logo && <img src={sf.logo} alt="preview" className="h-10 mb-3 object-contain" />}

            <div className="flex items-center gap-2 mt-4 mb-2"><Cloud size={14} className="text-cyan-400" /><span className="font-heading font-bold text-slate-200 text-[13px]">Microsoft Entra (per-customer)</span></div>
            <p className="text-[11px] text-slate-500 mb-2">This customer's own Azure app registration (needs <span className="font-mono">User.Read.All</span>). Falls back to global credentials if left blank.</p>
            <label className="lbl">Tenant ID</label>
            <input data-testid="cust-azure-tenant" value={sf.azure_tenant_id} onChange={(e) => setSf({ ...sf, azure_tenant_id: e.target.value })} className="inp2 font-mono" />
            <label className="lbl">Client ID</label>
            <input data-testid="cust-azure-client" value={sf.azure_client_id} onChange={(e) => setSf({ ...sf, azure_client_id: e.target.value })} className="inp2 font-mono" />
            <label className="lbl">Client Secret {c.azure_secret_set && <span className="text-emerald-400">(secret set — leave blank to keep)</span>}</label>
            <input data-testid="cust-azure-secret" type="password" value={sf.azure_client_secret} onChange={(e) => setSf({ ...sf, azure_client_secret: e.target.value })} className="inp2 font-mono" placeholder="••••••••" />
          </div>
          <div className="flex justify-end gap-2 mt-4"><Btn variant="ghost" onClick={() => setShowSettings(false)}>Cancel</Btn><Btn data-testid="cust-settings-save" onClick={saveSettings} disabled={!sf.name}>Save</Btn></div>
          <style>{`.inp2{width:100%;background:#0b1220;border:1px solid #334155;border-radius:6px;padding:8px 12px;font-size:13px;color:#e2e8f0;outline:none;margin-bottom:12px}.lbl{display:block;font-size:12px;color:#94a3b8;margin-bottom:4px}`}</style>
        </Modal>
      )}

      {showSite && (
        <Modal title="Add Site" onClose={() => setShowSite(false)}>
          <label className="text-[12px] text-slate-400">Site name</label>
          <input data-testid="site-name-input" value={siteName} onChange={(e) => setSiteName(e.target.value)} autoFocus
            className="mt-1 mb-4 w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-cyan-500" />
          <div className="flex justify-end gap-2"><Btn variant="ghost" onClick={() => setShowSite(false)}>Cancel</Btn><Btn data-testid="site-save-btn" onClick={addSite} disabled={!siteName}>Add</Btn></div>
        </Modal>
      )}

      {showTok && (
        <Modal title="Generate Enrollment Token" onClose={() => setShowTok(false)}>
          {!tok ? (
            <>
              <label className="text-[12px] text-slate-400">Assign to site (optional)</label>
              <select value={tokSite} onChange={(e) => setTokSite(e.target.value)}
                className="mt-1 mb-4 w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-cyan-500">
                <option value="">— none —</option>
                {c.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <p className="text-[12px] text-slate-500 mb-4">Token valid 24h · up to 5 devices · single-use per device.</p>
              <div className="flex justify-end gap-2"><Btn variant="ghost" onClick={() => setShowTok(false)}>Cancel</Btn><Btn data-testid="token-generate-btn" onClick={genToken}>Generate</Btn></div>
            </>
          ) : (
            <>
              <p className="text-[13px] text-slate-400 mb-2">Silent install on a Windows endpoint:</p>
              <pre className="bg-slate-950 border border-slate-800 rounded-md p-3 text-[11px] text-cyan-300 overflow-x-auto whitespace-pre-wrap break-all">msiexec /i mimoIT-Control-Agent-x64.msi /qn ENROLLMENT_TOKEN={tok}</pre>
              <p className="text-[13px] text-slate-400 mt-3 mb-1">Or simulate a device now:</p>
              <pre className="bg-slate-950 border border-slate-800 rounded-md p-3 text-[11px] text-emerald-300 overflow-x-auto whitespace-pre-wrap break-all">python /app/agent_simulator/simulator.py --url {process.env.REACT_APP_BACKEND_URL} --token {tok} --hostname WIN11-DEMO-01</pre>
              <Btn variant="ghost" className="mt-3" onClick={() => { navigator.clipboard.writeText(tok); toast.success("Token copied"); }}><Copy size={14} /> Copy token</Btn>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}

function Tbl({ rows, cols, onRow, empty, testid }) {
  if (!rows || rows.length === 0) return <div className="card-surface rounded-lg p-8 text-center text-slate-500 text-[13px]">{empty}</div>;
  return (
    <div className="card-surface rounded-lg overflow-hidden overflow-x-auto" data-testid={testid}>
      <table className="w-full text-left text-[13px]">
        <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/80 border-b border-slate-800">
          {cols.map(([, label]) => <th key={label} className="px-3 py-2">{label}</th>)}</tr></thead>
        <tbody className="divide-y divide-slate-800/60">
          {rows.map((r, i) => (
            <tr key={r.id || i} onClick={() => onRow && onRow(r)} className={"hover-row " + (onRow ? "cursor-pointer" : "")} data-testid={`${testid}-row`}>
              {cols.map(([key, label]) => <td key={label} className="px-3 py-2 text-slate-300 truncate max-w-xs">{String(r[key] ?? "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CustomerEntra({ id, canEdit, onSynced }) {
  const [st, setSt] = useState(null);
  const [gst, setGst] = useState(null);
  const [hist, setHist] = useState([]);
  const [cfg, setCfg] = useState(false);
  const [gcfg, setGcfg] = useState(false);
  const [f, setF] = useState({ tenant_id: "", client_id: "", client_secret: "" });
  const [gf, setGf] = useState({ domain: "", subject: "", service_account_json: "" });
  const [syncing, setSyncing] = useState(false);
  const [imp, setImp] = useState(false);
  const [csv, setCsv] = useState("name,email,title,department\n");
  const load = () => {
    api.get(`/customers/${id}/entra`).then((r) => { setSt(r.data); setF({ tenant_id: r.data.tenant_id || "", client_id: r.data.client_id || "", client_secret: "" }); }).catch(() => {});
    api.get(`/customers/${id}/google`).then((r) => { setGst(r.data); setGf({ domain: r.data.domain || "", subject: r.data.subject || "", service_account_json: "" }); }).catch(() => {});
    api.get(`/customers/${id}/sync-history`).then((r) => setHist(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [id]);
  const gsave = async () => { try { await api.put(`/customers/${id}/google`, gf); toast.success("Google Workspace saved"); setGcfg(false); load(); } catch (e) { toast.error(apiErr(e)); } };
  const gdisc = async () => { if (!window.confirm("Disconnect Google Workspace?")) return; try { await api.put(`/customers/${id}/google`, { disconnect: true }); toast.success("Google disconnected"); setGcfg(false); load(); } catch (e) { toast.error(apiErr(e)); } };
  const gsync = async () => { setSyncing(true); try { const r = await api.post("/people/google-sync", { customer_id: id }); if (r.data.configured) toast.success(`Synced ${r.data.synced} user(s) from Google` + (r.data.auto_linked ? ` · linked ${r.data.auto_linked} device(s)` : "")); else toast.error(r.data.note || "Google not configured"); } catch (e) { toast.error(apiErr(e)); } finally { load(); onSynced && onSynced(); setSyncing(false); } };
  const save = async () => { try { await api.put(`/customers/${id}/entra`, f); toast.success("Entra connection saved"); setCfg(false); load(); } catch (e) { toast.error(apiErr(e)); } };
  const disconnect = async () => { if (!window.confirm("Disconnect Entra for this customer?")) return; try { await api.put(`/customers/${id}/entra`, { disconnect: true }); toast.success("Entra disconnected"); setCfg(false); load(); } catch (e) { toast.error(apiErr(e)); } };
  const sync = async () => { setSyncing(true); try { const r = await api.post("/people/azure-sync", { customer_id: id }); if (r.data.configured) toast.success(`Synced ${r.data.synced} user${r.data.synced === 1 ? "" : "s"} from Entra` + (r.data.auto_linked ? ` · linked ${r.data.auto_linked} device(s)` : "")); else toast.error(r.data.note || "Entra not configured"); } catch (e) { toast.error(apiErr(e)); } finally { load(); onSynced && onSynced(); setSyncing(false); } };
  const runImport = async () => { try { const r = await api.post("/people/import-csv", { customer_id: id, csv }); toast.success(`Imported ${r.data.added}`); setImp(false); } catch (e) { toast.error(apiErr(e)); } };
  if (!st) return null;
  const connected = st.configured;
  return (
    <div className="card-surface rounded-lg p-4 mb-4" data-testid="cust-entra-panel">
      <style>{`.lbl{display:block;font-size:12px;color:#94a3b8;margin:6px 0 3px}.inp{width:100%;background:#0b1220;border:1px solid #334155;border-radius:6px;padding:8px 12px;font-size:13px;color:#e2e8f0;outline:none;margin-bottom:8px}`}</style>
      <div className="flex items-center gap-2 flex-wrap">
        <Cloud size={16} className={connected ? "text-emerald-400" : "text-slate-500"} />
        <span className="font-heading font-bold text-slate-200 text-[14px]">Directory Sync</span>
        <span data-testid="entra-status" className={"text-[11px] rounded px-2 py-0.5 " + (connected ? "bg-emerald-950/50 text-emerald-300" : st.using_global_fallback ? "bg-sky-950/50 text-sky-300" : "bg-slate-800 text-slate-400")}>
          {connected ? "Microsoft Entra connected" : st.using_global_fallback ? "Using shared Entra credentials" : "Not connected"}
        </span>
        {st.last && <span className="text-[11px] text-slate-500">Last sync: {st.last.synced} users · {timeAgo(st.last.at)}</span>}
        <div className="flex-1" />
        {canEdit && <Btn variant="ghost" data-testid="entra-sync-now" onClick={sync} disabled={syncing}><Cloud size={14} /> {syncing ? "Syncing…" : "Sync now"}</Btn>}
        {canEdit && <Btn variant="ghost" data-testid="entra-configure" onClick={() => setCfg(true)}><KeyRound size={14} /> {connected ? "Manage Entra" : "Connect Entra"}</Btn>}
        {canEdit && <Btn variant="ghost" data-testid="entra-import-csv" onClick={() => setImp(true)}><Upload size={14} /> Import CSV</Btn>}
      </div>
      <p className="text-[12px] text-slate-500 mt-2">Each customer connects their own Microsoft Entra (Azure AD) tenant. Customers on Google or without SSO can import users from CSV instead.</p>

      {st.last_error && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-rose-900/60 bg-rose-950/40 px-3 py-2" data-testid="sync-error-banner">
          <AlertTriangle size={15} className="text-rose-400 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="text-[12px] font-semibold text-rose-300">
              Last {st.last_error_provider === "google" ? "Google Workspace" : "Microsoft Entra"} sync failed
              {st.last_error_at && <span className="ml-1 font-normal text-rose-400/70">· {timeAgo(st.last_error_at)}</span>}
            </div>
            <div className="text-[12px] text-rose-200/80 break-words">{st.last_error}</div>
            <div className="text-[11px] text-rose-400/60 mt-0.5">Check the tenant credentials / delegation, then run Sync again.</div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-slate-800/70" data-testid="cust-google-row">
        <Cloud size={16} className={gst && gst.configured ? "text-emerald-400" : "text-slate-500"} />
        <span className="font-heading font-bold text-slate-200 text-[14px]">Google Workspace</span>
        <span data-testid="google-status" className={"text-[11px] rounded px-2 py-0.5 " + (gst && gst.configured ? "bg-emerald-950/50 text-emerald-300" : "bg-slate-800 text-slate-400")}>{gst && gst.configured ? "Google connected" : "Not connected"}</span>
        <div className="flex-1" />
        {canEdit && gst && gst.configured && <Btn variant="ghost" data-testid="google-sync-now" onClick={gsync} disabled={syncing}><Cloud size={14} /> {syncing ? "Syncing…" : "Sync now"}</Btn>}
        {canEdit && <Btn variant="ghost" data-testid="google-configure" onClick={() => setGcfg(true)}><KeyRound size={14} /> {gst && gst.configured ? "Manage Google" : "Connect Google"}</Btn>}
      </div>

      {hist.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-800/70" data-testid="sync-history">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Recent syncs</div>
          <div className="space-y-1">{hist.slice(0, 8).map((h) => (
            <div key={h.id} className="flex items-center gap-2 text-[12px] text-slate-400" data-testid="sync-history-row">
              <span className={"rounded px-1.5 py-0.5 text-[10px] " + (h.provider === "google" ? "bg-sky-950/50 text-sky-300" : "bg-cyan-950/50 text-cyan-300")}>{h.provider}</span>
              <span className="text-slate-300">{h.synced} users</span>
              <span className="text-slate-600">(+{h.added || 0} new, {h.updated || 0} updated)</span>
              <span className="ml-auto text-slate-600">{h.by} · {timeAgo(h.at)}</span>
            </div>
          ))}</div>
        </div>
      )}

      {cfg && (
        <Modal title="Microsoft Entra connection" onClose={() => setCfg(false)}>
          <p className="text-[12px] text-slate-500 mb-3">App registration with <b>User.Read.All</b> (application permission, admin-consented). Leave the secret blank to keep the existing one.</p>
          <label className="lbl">Directory (tenant) ID</label>
          <input data-testid="entra-tenant" value={f.tenant_id} onChange={(e) => setF({ ...f, tenant_id: e.target.value })} className="inp" style={{ fontFamily: "monospace" }} placeholder="00000000-0000-0000-0000-000000000000" />
          <label className="lbl">Application (client) ID</label>
          <input data-testid="entra-client" value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} className="inp" style={{ fontFamily: "monospace" }} placeholder="app registration client id" />
          <label className="lbl">Client secret {st.secret_set && <span className="text-emerald-400">(set)</span>}</label>
          <input data-testid="entra-secret" type="password" value={f.client_secret} onChange={(e) => setF({ ...f, client_secret: e.target.value })} className="inp" placeholder={st.secret_set ? "•••••••• (leave blank to keep)" : "client secret value"} />
          <div className="flex justify-between items-center mt-4">
            {connected ? <button data-testid="entra-disconnect" onClick={disconnect} className="text-[12px] text-rose-400 hover:text-rose-300">Disconnect</button> : <span />}
            <div className="flex gap-2"><Btn variant="ghost" onClick={() => setCfg(false)}>Cancel</Btn><Btn data-testid="entra-save" onClick={save} disabled={!f.tenant_id || !f.client_id}>Save</Btn></div>
          </div>
        </Modal>
      )}
      {imp && (
        <Modal title="Import users from CSV" onClose={() => setImp(false)}>
          <p className="text-[12px] text-slate-500 mb-2">First row must be headers: <code className="text-cyan-300">name,email,title,department</code></p>
          <textarea data-testid="entra-csv" value={csv} onChange={(e) => setCsv(e.target.value)} rows={8} className="inp" style={{ fontFamily: "monospace" }} />
          <div className="flex justify-end gap-2 mt-3"><Btn variant="ghost" onClick={() => setImp(false)}>Cancel</Btn><Btn data-testid="entra-csv-import" onClick={runImport}>Import</Btn></div>
        </Modal>
      )}
      {gcfg && gst && (
        <Modal title="Google Workspace connection" onClose={() => setGcfg(false)}>
          <p className="text-[12px] text-slate-500 mb-3">Service account (Admin SDK, domain-wide delegation for <b>admin.directory.user.readonly</b>) + an admin email to impersonate. Paste the service account JSON key.</p>
          <label className="lbl">Workspace domain (optional)</label>
          <input data-testid="google-domain" value={gf.domain} onChange={(e) => setGf({ ...gf, domain: e.target.value })} className="inp" placeholder="example.com (blank = all domains)" />
          <label className="lbl">Admin email to impersonate</label>
          <input data-testid="google-subject" value={gf.subject} onChange={(e) => setGf({ ...gf, subject: e.target.value })} className="inp" placeholder="admin@example.com" />
          <label className="lbl">Service account JSON {gst.sa_set && <span className="text-emerald-400">(set)</span>}</label>
          <textarea data-testid="google-sa" value={gf.service_account_json} onChange={(e) => setGf({ ...gf, service_account_json: e.target.value })} rows={7} className="inp" style={{ fontFamily: "monospace" }} placeholder={gst.sa_set ? '{ … } (leave blank to keep existing)' : '{ "type": "service_account", … }'} />
          <div className="flex justify-between items-center mt-4">
            {gst.configured ? <button data-testid="google-disconnect" onClick={gdisc} className="text-[12px] text-rose-400 hover:text-rose-300">Disconnect</button> : <span />}
            <div className="flex gap-2"><Btn variant="ghost" onClick={() => setGcfg(false)}>Cancel</Btn><Btn data-testid="google-save" onClick={gsave} disabled={!gf.subject}>Save</Btn></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
function AutoLinkReport({ id, nonce, canEdit }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get(`/customers/${id}/auto-link-report`).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, nonce]);
  const undo = async (did) => {
    if (!window.confirm("Undo this auto-link? The person will be unassigned and this device won't be auto-linked again.")) return;
    try { await api.post(`/devices/${did}/auto-link-undo`); toast.success("Auto-link removed"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!rows.length) return null;
  return (
    <div className="card-surface rounded-lg p-4 mb-4" data-testid="auto-link-report">
      <div className="flex items-center gap-2 mb-2">
        <LinkIcon size={15} className="text-cyan-400" />
        <span className="font-heading font-bold text-slate-200 text-[14px]">Auto-linked devices</span>
        <span className="text-[11px] text-slate-500">{rows.length} matched from sign-in</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[13px]">
          <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
            <th className="px-2 py-1.5">Device</th><th className="px-2 py-1.5">Signed-in user</th>
            <th className="px-2 py-1.5">Linked person</th><th className="px-2 py-1.5">Match</th>
            <th className="px-2 py-1.5">When</th><th className="px-2 py-1.5 text-right">Review</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-800/60">
            {rows.map((r) => (
              <tr key={r.id} className="hover-row" data-testid="auto-link-row">
                <td className="px-2 py-1.5 text-slate-200 font-mono truncate max-w-[140px]">{r.hostname || "—"}</td>
                <td className="px-2 py-1.5 text-slate-400 font-mono truncate max-w-[140px]">{r.logged_in_user || "—"}</td>
                <td className="px-2 py-1.5 text-slate-300 truncate max-w-[180px]">{r.contact_name}<span className="text-slate-600 ml-1">{r.contact_email}</span></td>
                <td className="px-2 py-1.5"><span className={"text-[10px] rounded px-1.5 py-0.5 " + (r.matched_via === "upn" ? "bg-emerald-950/50 text-emerald-300" : "bg-slate-800 text-slate-400")}>{r.matched_via === "upn" ? "UPN" : "username"}</span></td>
                <td className="px-2 py-1.5 text-slate-500">{timeAgo(r.at)}</td>
                <td className="px-2 py-1.5 text-right">
                  {r.still_linked
                    ? (canEdit && <button data-testid={`auto-link-undo-${r.device_id}`} onClick={() => undo(r.device_id)} className="text-[12px] text-rose-400 hover:text-rose-300">Undo</button>)
                    : <span className="text-[11px] text-slate-600">changed since</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DirectoryGaps({ id, nonce, onNav }) {
  const [g, setG] = useState(null);
  useEffect(() => { api.get(`/customers/${id}/directory-gaps`).then((r) => setG(r.data)).catch(() => setG(null)); }, [id, nonce]);
  if (!g) return null;
  const ppl = g.people_without_device || [], dev = g.devices_without_match || [];
  return (
    <div className="card-surface rounded-lg p-4 mb-4" data-testid="directory-gaps">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle size={15} className={ppl.length || dev.length ? "text-amber-400" : "text-emerald-400"} />
        <span className="font-heading font-bold text-slate-200 text-[14px]">Coverage gaps</span>
        {!ppl.length && !dev.length && <span className="text-[12px] text-emerald-400">Everything is matched up</span>}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">People with no device ({ppl.length})</div>
          {ppl.length === 0 ? <div className="text-[12px] text-slate-600">None</div> : (
            <div className="space-y-1 max-h-56 overflow-y-auto">{ppl.map((p) => (
              <div key={p.id} data-testid="gap-person-row" onClick={() => onNav && onNav(`/people/${p.id}`)}
                className="flex items-center gap-2 text-[12px] text-slate-300 rounded px-2 py-1 hover:bg-slate-800/40 cursor-pointer">
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <span className="truncate shrink-0 max-w-[45%]">{p.name}</span>
                  <span className="text-slate-600 truncate">{p.email}</span>
                </div>
                <span className="shrink-0 text-[10px] rounded px-1.5 py-0.5 bg-slate-800 text-slate-400">{p.source}</span>
              </div>
            ))}</div>
          )}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Devices whose sign-in matched nobody ({dev.length})</div>
          {dev.length === 0 ? <div className="text-[12px] text-slate-600">None</div> : (
            <div className="space-y-1 max-h-56 overflow-y-auto">{dev.map((d) => (
              <div key={d.id} data-testid="gap-device-row" onClick={() => onNav && onNav(`/devices/${d.id}`)}
                className="flex items-center gap-2 text-[12px] text-slate-300 rounded px-2 py-1 hover:bg-slate-800/40 cursor-pointer">
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <span className="font-mono truncate shrink-0 max-w-[45%]">{d.hostname}</span>
                  <span className="text-slate-600 font-mono truncate">{d.logged_in_user}</span>
                </div>
                <span className={"shrink-0 text-[10px] " + (d.status === "ONLINE" ? "text-emerald-400" : "text-slate-500")}>{d.status}</span>
              </div>
            ))}</div>
          )}
        </div>
      </div>
    </div>
  );
}


function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="card-surface rounded-xl p-5 w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-slate-100">{title}</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
